import React from "react";
import { SERVICE_DIRECTIONS, directionCopy } from "../import/intent";

const DirectionChooser = ({ value, suggestion, onChange, stats }) => {
  const options = [SERVICE_DIRECTIONS.BOTH, SERVICE_DIRECTIONS.PICKUP, SERVICE_DIRECTIONS.DROPOFF];
  const morning = Math.max(stats?.pickupRouteNameCount || 0, stats?.routeListPickupCount || 0);
  const afternoon = Math.max(stats?.dropoffRouteNameCount || 0, stats?.routeListDropoffCount || 0);
  const fromFile = Boolean(suggestion?.fromFile);
  return (
    <section className="start-ask" aria-labelledby="start-direction-title">
      <h3 id="start-direction-title">{fromFile ? "Which of the file’s services should we create?" : "Which services should we create?"}</h3>
      {fromFile && (
        <p className="start-muted start-ask__lead">
          Your file already defines {morning ? `${morning} morning` : ""}
          {morning && afternoon ? " and " : ""}
          {afternoon ? `${afternoon} afternoon` : ""} service{morning + afternoon === 1 ? "" : "s"}
          {stats?.routeListLegCount ? ", each with its own times and vehicle" : ""}. We create exactly those — nothing is invented.
        </p>
      )}
      <div className="start-ask__options" role="radiogroup" aria-label="Service direction">
        {options.map((option) => {
          const copy = directionCopy(option);
          const selected = value === option;
          const suggested = suggestion?.suggested === option;
          let title = copy.title;
          let detail = copy.detail;
          if (fromFile && option === SERVICE_DIRECTIONS.BOTH && morning && afternoon) {
            title = "As in the file";
            detail = `${morning} morning + ${afternoon} afternoon services.`;
          } else if (option === SERVICE_DIRECTIONS.PICKUP && morning) {
            detail = `${morning} morning service${morning === 1 ? "" : "s"} from the file; afternoon ones are skipped.`;
          } else if (option === SERVICE_DIRECTIONS.DROPOFF && afternoon) {
            detail = `${afternoon} afternoon service${afternoon === 1 ? "" : "s"} from the file; morning ones are skipped.`;
          }
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`start-ask__option${selected ? " is-selected" : ""}`}
              onClick={() => onChange(option)}
            >
              <span className="start-ask__radio" aria-hidden="true" />
              <span className="start-ask__body">
                <strong>
                  {title}
                  {suggested && <em className="start-ask__tag">Suggested</em>}
                </strong>
                <span>{detail}</span>
              </span>
            </button>
          );
        })}
      </div>
      {suggestion?.reason && <p className="start-muted start-ask__reason">{suggestion.reason}</p>}
    </section>
  );
};

const StagePrepare = ({
  preview,
  source,
  extraction,
  schoolName,
  schoolAddress,
  schoolDecision,
  schoolRequired,
  maxPassengers,
  serviceDirection,
  directionSuggestion,
  onServiceDirection,
  onMaxPassengers,
  onNext,
  onBack,
  canAdvance,
}) => {
  if (!preview) return null;
  const missing = preview.students.filter((student) => student.missing.length > 0);
  const isUpload = source === "upload";
  const stats = extraction?.stats || {};

  return (
    <section className="start-stage">
      <header className="start-stage__header">
        <p className="start-kicker">{isUpload ? "Services" : "Operation draft"}</p>
        <h2>{isUpload ? "Set up the services" : "Students, schools, and decisions"}</h2>
        <p>
          {source === "sample"
            ? "The sample represents a three-school morning operation. Missing fields are intentional so you can see how RouteBot surfaces gaps."
            : "Two quick choices: which direction the services run, and how many passengers fit in one vehicle. Passengers keep the service named in their row; the rest are grouped by school."}
        </p>
      </header>

      {isUpload && (
        <DirectionChooser value={serviceDirection} suggestion={directionSuggestion} onChange={onServiceDirection} stats={stats} />
      )}

      {isUpload && (
        <div className="start-capacity">
          {schoolDecision === "later" && (
            <p className="start-note">Students go under “Default School” for now — rename it or move them whenever you like.</p>
          )}
          {(schoolDecision === "create" || schoolDecision === "found" || schoolDecision === "existing" || schoolRequired) && (
            <p className="start-note">
              {preview.schools.length > 1
                ? `${preview.schools.length} schools: ${preview.schools.map((school) => `${school.name} (${school.count})`).join(", ")}`
                : `School: ${schoolName || extraction?.schools?.[0]?.name || "Not set"}${schoolAddress ? ` — ${schoolAddress}` : ""}`}
            </p>
          )}
          <label>
            <span>{stats.distinctRouteNameCount > 0 ? "Vehicle capacity for passengers without a service name" : "Maximum passengers on one vehicle"}</span>
            <input
              type="number"
              min={4}
              max={50}
              value={maxPassengers}
              onChange={(event) => onMaxPassengers(Number(event.target.value))}
            />
          </label>
          {stats.distinctRouteNameCount > 0 && (
            <p className="start-muted">
              Services named in the file are kept as they are, even when they carry more riders than this. Only passengers without a
              service name are grouped by this number.
            </p>
          )}
        </div>
      )}

      {!isUpload && (
        <div className="start-school-grid">
          {preview.schools.map((school) => (
            <article key={school.name} className="start-school-card">
              <h3>{school.name}</h3>
              <p>
                {school.count} student{school.count === 1 ? "" : "s"}
              </p>
              {school.missingAddress > 0 && (
                <span className="start-pill start-pill--warn">{school.missingAddress} missing addresses</span>
              )}
            </article>
          ))}
        </div>
      )}

      {isUpload ? (
        missing.filter((student) => student.missing.includes("address")).length > 0 && (
          <aside className="start-missing">
            <h3>Without an address</h3>
            <p className="start-muted">
              {missing.filter((student) => student.missing.includes("address")).length} passengers have no address. They still go on a
              service, but the map cannot place them until an address is added.
            </p>
          </aside>
        )
      ) : (
        <div className="start-split">
          <div className="start-table-wrap">
            <h3>Recognized students</h3>
            <table className="start-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>School</th>
                  <th>Parent</th>
                  <th>Phone</th>
                  <th>Address</th>
                </tr>
              </thead>
              <tbody>
                {preview.students.slice(0, 10).map((student) => (
                  <tr key={student.id}>
                    <td>{student.name}</td>
                    <td>{student.school}</td>
                    <td>{student.parent || "—"}</td>
                    <td>{student.phone || "—"}</td>
                    <td>{student.address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <aside className="start-missing">
            <h3>Needs a decision</h3>
            {missing.length === 0 ? (
              <p className="start-muted">No required fields are missing.</p>
            ) : (
              <ul>
                {missing.slice(0, 8).map((student) => (
                  <li key={student.id}>
                    <strong>{student.name}</strong>
                    <span>Missing {student.missing.join(", ")}</span>
                  </li>
                ))}
              </ul>
            )}
            {missing.length > 8 && <p className="start-muted">{missing.length - 8} more rows with gaps.</p>}
          </aside>
        </div>
      )}

      <div className="start-stage__nav">
        <button type="button" className="start-btn start-btn--ghost" onClick={onBack}>
          Back
        </button>
        <button type="button" className="start-btn" onClick={onNext} disabled={isUpload && canAdvance === false}>
          {isUpload ? "Preview the services" : "Distribute onto vehicles"}
        </button>
      </div>
    </section>
  );
};

export default StagePrepare;
